/**
 * Measure the box a composition actually lives in.
 *
 * Spec §7.1 is explicit that every threshold is a query on **available content
 * width**, not the viewport, because the native app branches on measured layout
 * size — under Split View and Stage Manager that is the window, not the screen.
 * The web equivalent of "measured layout size" is the element's own content
 * box, which is what `ResizeObserver` reports and what `container-type:
 * inline-size` queries against, so the JS branch and any `@container` rule a
 * feature writes agree on the same number.
 */

import { useCallback, useState } from "react";

export interface ContainerSize {
  /** Content-box inline size in CSS px. */
  readonly width: number;
  /** Content-box block size in CSS px. */
  readonly height: number;
}

export interface ContainerMeasurement<T extends Element> {
  /**
   * A callback ref, not a ref object: a ref object never re-renders on attach,
   * so a measurement started from one always misses the first commit.
   */
  readonly ref: (node: T | null) => void;
  /**
   * `null` until the element has been measured. Callers must treat `null` as
   * "unknown", never as zero — a zero width resolves to the compact class and
   * would flash the phone composition into a desktop pane.
   */
  readonly size: ContainerSize | null;
}

function sameSize(previous: ContainerSize | null, next: ContainerSize): boolean {
  return previous !== null && previous.width === next.width && previous.height === next.height;
}

/**
 * `ResizeObserver` is one of the two globals §1.1 of the seam plan permits in
 * this folder. Its absence is treated as "measure once and stop" rather than as
 * an error: an environment without it (an older test runner, a prerender) still
 * gets a correct first measurement from the element it was handed, and losing
 * live updates is a degradation, not a failure.
 */
function observerConstructor(): typeof ResizeObserver | null {
  const candidate: unknown = (globalThis as { ResizeObserver?: unknown }).ResizeObserver;
  return typeof candidate === "function" ? (candidate as typeof ResizeObserver) : null;
}

export function useContainerWidth<T extends Element = HTMLElement>(): ContainerMeasurement<T> {
  const [size, setSize] = useState<ContainerSize | null>(null);

  const ref = useCallback((node: T | null) => {
    if (node === null) {
      // Deliberately not clearing the size: React detaches and reattaches the
      // ref on every commit that changes the element, and clearing would send
      // every consumer through the "unknown" branch mid-interaction.
      return;
    }

    const publish = (width: number, height: number): void => {
      const next: ContainerSize = { width, height };
      setSize((previous) => (sameSize(previous, next) ? previous : next));
    };

    const rect = node.getBoundingClientRect();
    publish(rect.width, rect.height);

    const Observer = observerConstructor();
    if (Observer === null) return;

    const observer = new Observer((entries) => {
      for (const entry of entries) {
        // `contentBoxSize` is the same box `@container` queries measure;
        // `contentRect` is the fallback for engines that predate it.
        const box = entry.contentBoxSize[0];
        if (box === undefined) {
          publish(entry.contentRect.width, entry.contentRect.height);
        } else {
          publish(box.inlineSize, box.blockSize);
        }
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}
